/**
 * AdminRevenueTab - Revenue breakdown by plan, period, and channel.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { safeLocaleString } from '@/lib/utils';
import {
  getDashboardRevenue,
  type RevenueRow,
} from '@/services/adminDashboardService';

const PERIODS = [
  { value: '7d', label: '7 dias' },
  { value: '14d', label: '14 dias' },
  { value: '30d', label: '30 dias' },
  { value: '90d', label: '90 dias' },
];

const AdminRevenueTab: React.FC = () => {
  const [period, setPeriod] = useState('30d');
  const [channel, setChannel] = useState('');
  const [rows, setRows] = useState<RevenueRow[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDashboardRevenue(period);
      setRows(res.data || []);
    } catch (err) {
      console.error('Failed to fetch revenue data', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Extract unique channels from data
  const availableChannels = useMemo(() => {
    const set = new Set<string>();
    for (const row of rows) {
      if (row.channel) set.add(row.channel);
    }
    return Array.from(set).sort();
  }, [rows]);

  // Filter rows by selected channel
  const filteredRows = useMemo(() => {
    if (!channel) return rows;
    return rows.filter((r) => r.channel === channel);
  }, [rows, channel]);

  if (loading) {
    return <div className="text-muted-foreground py-8 text-center">Carregando dados...</div>;
  }

  // Aggregate by plan
  const byPlan = new Map<string, { revenue: number; count: number }>();
  const byPeriod = new Map<string, { revenue: number; count: number }>();
  const byChannel = new Map<string, { revenue: number; count: number }>();
  let totalRevenue = 0;
  let totalPayments = 0;

  for (const row of filteredRows) {
    totalRevenue += row.total_revenue;
    totalPayments += row.payment_count;

    const plan = byPlan.get(row.plan_name) || { revenue: 0, count: 0 };
    plan.revenue += row.total_revenue;
    plan.count += row.payment_count;
    byPlan.set(row.plan_name, plan);

    const per = byPeriod.get(row.period) || { revenue: 0, count: 0 };
    per.revenue += row.total_revenue;
    per.count += row.payment_count;
    byPeriod.set(row.period, per);

    const ch = byChannel.get(row.channel) || { revenue: 0, count: 0 };
    ch.revenue += row.total_revenue;
    ch.count += row.payment_count;
    byChannel.set(row.channel, ch);
  }

  const ticketMedio = totalPayments > 0 ? totalRevenue / totalPayments : 0;

  const periodLabels: Record<string, string> = {
    MONTHLY: 'Mensal',
    QUARTERLY: 'Trimestral',
    YEARLY: 'Anual',
  };

  return (
    <div className="space-y-6">
      {/* Period selector + Channel filter */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex gap-2">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                period === p.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <select
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
          className="px-3 py-1.5 text-sm rounded-md border border-border bg-background text-foreground"
        >
          <option value="">Todos os canais</option>
          {availableChannels.map((ch) => (
            <option key={ch} value={ch}>
              {ch}
            </option>
          ))}
        </select>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Receita Total" value={`R$ ${safeLocaleString(totalRevenue, 'pt-BR', { minimumFractionDigits: 2 })}`} />
        <KpiCard label="Ticket Médio" value={`R$ ${safeLocaleString(ticketMedio, 'pt-BR', { minimumFractionDigits: 2 })}`} />
        <KpiCard label="Pagamentos" value={totalPayments} />
        <KpiCard label="Receita/Pagamento" value={totalPayments > 0 ? `R$ ${(totalRevenue / totalPayments).toFixed(2)}` : 'R$ 0'} />
      </div>

      {/* Revenue by Plan */}
      {byPlan.size > 0 && (
        <div className="border border-border rounded-lg overflow-hidden">
          <h3 className="text-sm font-semibold text-foreground p-4 border-b border-border">
            Receita por Plano
            {channel && <span className="text-muted-foreground font-normal"> — {channel}</span>}
          </h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50">
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Plano</th>
                <th className="text-right px-4 py-2 font-medium text-muted-foreground">Pagamentos</th>
                <th className="text-right px-4 py-2 font-medium text-muted-foreground">Receita</th>
                <th className="text-right px-4 py-2 font-medium text-muted-foreground">% do Total</th>
              </tr>
            </thead>
            <tbody>
              {Array.from(byPlan.entries())
                .sort((a, b) => b[1].revenue - a[1].revenue)
                .map(([name, data]) => (
                  <tr key={name} className="border-t border-border hover:bg-muted/30">
                    <td className="px-4 py-2 font-medium text-foreground">{name}</td>
                    <td className="text-right px-4 py-2 text-foreground">{data.count}</td>
                    <td className="text-right px-4 py-2 text-foreground">
                      R$ {safeLocaleString(data.revenue, 'pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="text-right px-4 py-2 text-foreground">
                      {totalRevenue > 0 ? ((data.revenue / totalRevenue) * 100).toFixed(1) : '0'}%
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Revenue by Billing Period */}
      {byPeriod.size > 0 && (
        <div className="border border-border rounded-lg overflow-hidden">
          <h3 className="text-sm font-semibold text-foreground p-4 border-b border-border">
            Receita por Período de Cobrança
            {channel && <span className="text-muted-foreground font-normal"> — {channel}</span>}
          </h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50">
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Período</th>
                <th className="text-right px-4 py-2 font-medium text-muted-foreground">Pagamentos</th>
                <th className="text-right px-4 py-2 font-medium text-muted-foreground">Receita</th>
                <th className="text-right px-4 py-2 font-medium text-muted-foreground">Ticket Médio</th>
              </tr>
            </thead>
            <tbody>
              {Array.from(byPeriod.entries())
                .sort((a, b) => b[1].revenue - a[1].revenue)
                .map(([per, data]) => (
                  <tr key={per} className="border-t border-border hover:bg-muted/30">
                    <td className="px-4 py-2 font-medium text-foreground">{periodLabels[per] || per}</td>
                    <td className="text-right px-4 py-2 text-foreground">{data.count}</td>
                    <td className="text-right px-4 py-2 text-foreground">
                      R$ {safeLocaleString(data.revenue, 'pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="text-right px-4 py-2 text-foreground">
                      R$ {data.count > 0 ? (data.revenue / data.count).toFixed(2) : '0'}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Revenue by Channel (only show when no channel filter) */}
      {!channel && byChannel.size > 0 && (
        <div className="border border-border rounded-lg overflow-hidden">
          <h3 className="text-sm font-semibold text-foreground p-4 border-b border-border">
            Receita por Canal de Aquisição
          </h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50">
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Canal</th>
                <th className="text-right px-4 py-2 font-medium text-muted-foreground">Pagamentos</th>
                <th className="text-right px-4 py-2 font-medium text-muted-foreground">Receita</th>
                <th className="text-right px-4 py-2 font-medium text-muted-foreground">% do Total</th>
              </tr>
            </thead>
            <tbody>
              {Array.from(byChannel.entries())
                .sort((a, b) => b[1].revenue - a[1].revenue)
                .map(([ch, data]) => (
                  <tr key={ch} className="border-t border-border hover:bg-muted/30 cursor-pointer" onClick={() => setChannel(ch)}>
                    <td className="px-4 py-2 font-medium text-foreground">{ch}</td>
                    <td className="text-right px-4 py-2 text-foreground">{data.count}</td>
                    <td className="text-right px-4 py-2 text-foreground">
                      R$ {safeLocaleString(data.revenue, 'pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="text-right px-4 py-2 text-foreground">
                      {totalRevenue > 0 ? ((data.revenue / totalRevenue) * 100).toFixed(1) : '0'}%
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {filteredRows.length === 0 && (
        <div className="text-muted-foreground py-8 text-center">
          Nenhum dado de receita neste período{channel ? ` para o canal "${channel}"` : ''}.
        </div>
      )}
    </div>
  );
};

function KpiCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="border border-border rounded-lg p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold text-foreground mt-1">{value}</div>
    </div>
  );
}

export default AdminRevenueTab;
